import {applyFiltersAsync} from "@wordpress/hooks";

import {SearchIntegration} from "./script/search-integration";
import {MarkerHandler} from "./script/marker-handler";
import {InfoWindowManager} from "./script/info-window-manager";
import defaultIcon from "./icons/default.svg?string";
import searchedLocationIcon from "./icons/searched-location.svg?string";

import "google.maps";

export type MarkerHandlerConfig = {
	minimumMarkers?: {
		count: number,
		types: string[]
	},
	maxSliceDistance: number, // In meters
	padding: number|google.maps.Padding,
	defaultBounds: google.maps.LatLngBoundsLiteral,
	iconMap: Partial<Record<string, Element|string>>,
	iconTyper(elementOrType: HTMLElement|string): string,
	buildIcon(elementOrType: HTMLElement|string): Element
};

(window as any).plauditBaseGoogleMapsAPIReady.then(async () => {
	const {AdvancedMarkerElement} = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;

	const setUpMap = async (mapElement: HTMLElement) => {
		mapElement.classList.toggle('plaudit-query-loop-map--setup', true);
		const queryLoopForm = mapElement.closest('form');
		if (!queryLoopForm) {
			return;
		}

		const map = new google.maps.Map(mapElement, await applyFiltersAsync("plaudit.query-loop.google-maps-integration.map-config", {
			maxZoom: 17, //This is an entirely arbitrary value determined by asking, "does it look nice?".
			mapId: mapElement.getAttribute('data-map-id'),
			zoomControl: true,
			scaleControl: true,
			mapTypeControl: false,
			streetViewControl: false
		} as google.maps.MapOptions, mapElement) as google.maps.MapOptions);
		(window as any).map = map;

		const infoWindowManager = new InfoWindowManager(map);

		const searchIntegration = new SearchIntegration(queryLoopForm);

		const markerHandlerConfig = (await applyFiltersAsync("plaudit.query-loop.google-maps-integration.marker-handler-config", {
			maxSliceDistance: 96_560.6, // 60 Miles
			padding: 20,
			defaultBounds: {north: 49.5, south: 43, east: -89, west: -97.5}, // A rough bounding box for Minnesota
			iconTyper: elementOrType => {
				return typeof elementOrType !== 'string' ? elementOrType.getAttribute('data-type') ?? 'default' : elementOrType;
			},
			iconMap: {
				"default": defaultIcon,
				"searched-location": searchedLocationIcon
			},
			buildIcon: elementOrType => {
				const iconType = markerHandlerConfig.iconTyper(elementOrType);
				let rawIconElement = markerHandlerConfig.iconMap[iconType];
				if (!rawIconElement) {
					console.error(`${markerHandlerConfig.iconTyper(elementOrType)} is not in the icon map.`
						+ ` Valid icon types are: ${Object.keys(markerHandlerConfig.iconMap).join(", ")}`);
					rawIconElement = markerHandlerConfig.iconMap['default']!; // 'default' is guaranteed to be set
				}

				if (typeof rawIconElement === 'string') {
					rawIconElement = new DOMParser().parseFromString(rawIconElement, 'image/svg+xml').documentElement;
					markerHandlerConfig.iconMap[iconType] = rawIconElement;
				}
				return rawIconElement.cloneNode(true) as Element;
			}
		} as MarkerHandlerConfig)) as MarkerHandlerConfig;

		if (!markerHandlerConfig.iconMap['default']) {
			// markerHandlerConfig.iconMap['default'] = new DOMParser().parseFromString(require("./map-marker.svg?string"), 'image/svg+xml').documentElement;
			console.error("A default icon has not been provided. This will likely cause crashes");
		}

		new MarkerHandler(queryLoopForm, markerHandlerConfig, infoWindowManager, searchIntegration, AdvancedMarkerElement, mapElement, map);
	};

	new MutationObserver(mutations => {
		for (const mutation of mutations) {
			for (const addedNode of mutation.addedNodes) {
				if (addedNode instanceof HTMLElement) {
					for (const mapElement of addedNode.querySelectorAll<HTMLElement>('.plaudit-query-loop-maps-google-map')) {
						setUpMap(mapElement);
					}
				}
			}
		}
	}).observe(document, {childList: true, subtree: true});

	await Promise.all([...document.querySelectorAll<HTMLElement>('.plaudit-query-loop-maps-google-map')].map(setUpMap));
});
